(() => {
  const instances = new WeakMap();
  const motion = window.matchMedia('(prefers-reduced-motion: reduce)');
  class ReviewCarousel {
    constructor(element) {
      this.element = element;
      this.slides = Array.from(element.querySelectorAll('[data-review-slide]'));
      this.dots = Array.from(element.querySelectorAll('[data-review-dot]'));
      this.status = element.querySelector('[data-review-status]');
      this.controls = element.querySelector('[data-review-controls]');
      this.viewport = element.querySelector('[data-review-slides]');
      this.track = element.querySelector('[data-review-track]');
      this.index = 0;
      this.position = 1;
      this.timer = null;
      this.settleTimer = null;
      this.visible = true;
      this.hovered = false;
      this.focused = false;
      this.moving = false;
      this.pointerStart = null;
      this.autoplay = element.dataset.autoplay === 'true';
      this.interval = Math.min(9000, Math.max(7000, Number(element.dataset.interval) || 9000));
      this.abort = new AbortController();
      this.signal = { signal: this.abort.signal };
      if (this.slides.length < 2) return;
      const clone = (slide) => {
        const result = slide.cloneNode(true);
        result.removeAttribute('data-review-slide');
        result.removeAttribute('data-shopify-editor-block');
        result.classList.remove('is-current');
        result.setAttribute('data-review-clone', '');
        result.setAttribute('aria-hidden', 'true');
        result.inert = true;
        result.querySelectorAll('[id]').forEach(node => node.removeAttribute('id'));
        return result;
      };
      this.track.prepend(clone(this.slides[this.slides.length - 1]));
      this.track.append(clone(this.slides[0]));
      this.place(false);
      this.controls.hidden = false;
      element.querySelector('[data-review-previous]').addEventListener('click', () => this.move(this.index - 1, true), this.signal);
      element.querySelector('[data-review-next]').addEventListener('click', () => this.move(this.index + 1, true), this.signal);
      this.dots.forEach((dot, index) => dot.addEventListener('click', () => this.move(index, true), this.signal));
      this.track.addEventListener('transitionend', (event) => {
        if (event.target === this.track && event.propertyName === 'transform') this.finish();
      }, this.signal);
      element.addEventListener('pointerenter', (event) => {
        if (event.pointerType === 'mouse') { this.hovered = true; this.schedule(); }
      }, this.signal);
      element.addEventListener('pointerleave', (event) => {
        if (event.pointerType === 'mouse') { this.hovered = false; this.schedule(); }
      }, this.signal);
      element.addEventListener('focusin', (event) => {
        this.focused = event.target.matches(':focus-visible');
        this.schedule();
      }, this.signal);
      element.addEventListener('focusout', (event) => {
        if (!element.contains(event.relatedTarget)) {
          this.focused = false;
          this.schedule();
        }
      }, this.signal);
      this.viewport.addEventListener('pointerdown', (event) => {
        if (event.pointerType === 'mouse') return;
        this.pointerStart = { x: event.clientX, y: event.clientY, id: event.pointerId };
      }, this.signal);
      this.viewport.addEventListener('pointerup', (event) => {
        const start = this.pointerStart;
        this.pointerStart = null;
        if (!start || start.id !== event.pointerId) return;
        const x = event.clientX - start.x;
        const y = event.clientY - start.y;
        if (Math.abs(x) > 45 && Math.abs(x) > Math.abs(y) * 1.5) this.move(this.index + (x < 0 ? 1 : -1), true);
      }, this.signal);
      this.viewport.addEventListener('pointercancel', () => { this.pointerStart = null; }, this.signal);
      document.addEventListener('visibilitychange', () => this.schedule(), this.signal);
      motion.addEventListener('change', () => {
        if (this.moving) this.finish();
        this.schedule();
      }, this.signal);
      this.observer = new IntersectionObserver((entries) => {
        this.visible = entries[0].isIntersecting;
        this.schedule();
      }, { threshold: 0.15 });
      this.observer.observe(element);
      this.schedule();
    }
    place(animate) {
      if (!animate) this.track.style.transition = 'none';
      this.track.style.transform = `translate3d(-${this.position * 100}%,0,0)`;
      if (!animate) {
        void this.track.offsetWidth;
        this.track.style.transition = '';
      }
    }
    move(index, manual) {
      if (this.moving || this.slides.length < 2) return;
      if (!manual && (motion.matches || this.hovered || this.focused || !this.visible || document.hidden)) return;
      const next = (index + this.slides.length) % this.slides.length;
      if (next === this.index) return;
      this.position = next + 1;
      if (this.index === this.slides.length - 1 && next === 0) this.position = this.slides.length + 1;
      if (this.index === 0 && next === this.slides.length - 1) this.position = 0;
      this.index = next;
      this.slides.forEach((slide, i) => {
        const selected = i === this.index;
        slide.classList.toggle('is-current', selected);
        slide.setAttribute('aria-hidden', String(!selected));
        slide.inert = !selected;
      });
      this.dots.forEach((dot, i) => dot.setAttribute('aria-pressed', String(i === this.index)));
      if (manual) this.status.textContent = this.element.dataset.positionLabel.replace('[number]', String(this.index + 1)).replace('[total]', String(this.slides.length));
      window.clearTimeout(this.timer);
      this.moving = !motion.matches;
      this.place(!motion.matches);
      if (this.moving) this.settleTimer = window.setTimeout(() => this.finish(), 850);
      else this.finish();
    }
    finish() {
      window.clearTimeout(this.settleTimer);
      if (this.position === 0 || this.position === this.slides.length + 1) {
        this.position = this.index + 1;
        this.place(false);
      }
      this.moving = false;
      this.schedule();
    }
    schedule() {
      window.clearTimeout(this.timer);
      if (this.slides.length < 2 || !this.autoplay || motion.matches || this.moving || this.hovered || this.focused || !this.visible || document.hidden) return;
      this.timer = window.setTimeout(() => this.move(this.index + 1, false), this.interval);
    }
    destroy() {
      window.clearTimeout(this.timer);
      window.clearTimeout(this.settleTimer);
      this.abort.abort();
      if (this.observer) this.observer.disconnect();
    }
  }
  function initialize(scope = document) {
    const elements = scope.matches?.('[data-sc-reviews-carousel]') ? [scope] : scope.querySelectorAll('[data-sc-reviews-carousel]');
    elements.forEach((element) => {
      if (!instances.has(element)) instances.set(element, new ReviewCarousel(element));
    });
  }
  initialize();
  document.addEventListener('shopify:section:load', (event) => initialize(event.target));
  document.addEventListener('shopify:section:unload', (event) => {
    event.target.querySelectorAll('[data-sc-reviews-carousel]').forEach((element) => {
      instances.get(element)?.destroy();
      instances.delete(element);
    });
  });
  document.addEventListener('shopify:block:select', (event) => {
    const slide = event.target.closest('[data-review-slide]');
    if (!slide) return;
    const carousel = instances.get(slide.closest('[data-sc-reviews-carousel]'));
    if (carousel) carousel.move(carousel.slides.indexOf(slide), true);
  });
})();
